import { cubicEaseOut, shouldAnimate, transition } from '@helpers/animate';
import { imageCoverContainer, imageWrapperPreserveRatio } from '@helpers/aspectRatio';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { Device, from } from '@helpers/media';
import scrollbars from '@helpers/scrollbars';
import ButtonStyles from '@stories/Components/Global/Buttons/Button/Button.styles';
import styled, { css } from 'styled-components';
const Container = styled.nav `
  display: none;

  --dropdownHeight: calc(100vh - var(--headerHeight));

  [data-scroll-active='true'] & {
    --dropdownHeight: calc(100vh - var(--stickyHeaderHeight) - var(--navigationHeight) - 16px);
  }

  @media ${from(Device.TabletLarge)} {
    display: flex;
    gap: ${fluid(0, 20, Device.TabletLarge)};
    justify-content: flex-end;
  }

  @keyframes fadeIn {
    0% {
      display: block;
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;
const NavigationLink = styled.a `
  ${transition('background-color, color')}
  ${fonts.daxWide.medium};

  font-size: ${fluid(14, 18, Device.TabletLarge)};
  line-height: ${fluid(24, 28, Device.TabletLarge)};
  display: flex;
  align-items: center;
  padding: ${fluid(14, 18, Device.TabletLarge)};
  text-decoration: none;
  background-color: var(--navigationBgColour);
  color: var(--navigationFgColour);
  text-align: center;

  &:focus {
    outline: none;
  }

  &.focus-ring {
    outline: auto;
    outline-offset: -3px;
    outline-width: 1px;
    outline-style: solid;
  }

  &.focus-ring,
  &[data-active='true'],
  &:hover {
    background-color: var(--navigationBgActiveColour);
    color: var(--navigationFgActiveColour);
  }

  &:last-of-type {
    margin-right: calc(${fluid(14, 18, Device.TabletLarge)} * -1);
  }
`;
const DropdownBackground = styled.div `
  display: none;
  opacity: 0;
  position: absolute;
  top: 100%;
  left: 50%;
  width: 100dvw;
  height: var(--dropdownHeight);
  background-color: var(--navigationBgActiveColour);
  transform: translateX(-50%);
  z-index: 2;

  &[data-active='true'] {
    display: block;
    opacity: 1;

    /* Use animation over transition due to change of display */
    ${shouldAnimate(css `
      animation: 300ms fadeIn ${cubicEaseOut};
    `)}
  }
`;
const NavigationDropdown = styled.div `
  ${scrollbars};

  display: none;
  opacity: 0;
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background-color: var(--navigationBgActiveColour);
  color: var(--navigationFgActiveColour);
  padding-right: ${fluid(22, 64)};
  height: var(--dropdownHeight);
  overflow: auto;
  z-index: 9;

  &::-webkit-scrollbar-thumb {
    [data-site='mep'] & {
      --fgColor: ${brand.global.red};
    }
  }

  &[data-active='true'] {
    display: block;
    opacity: 1;

    /* Use animation over transition due to change of display */
    ${shouldAnimate(css `
      animation: 300ms fadeIn ${cubicEaseOut};
    `)}
  }

  [data-site='ess'] & {
    padding-top: 1px;
    padding-bottom: 1px;
  }

  [data-site='mep'] & {
    padding-top: ${fluid(32, 44, Device.TabletLarge)};
    padding-bottom: ${fluid(32, 44, Device.TabletLarge)};
  }

  @media ${from(Device.TabletLarge)} {
    max-width: var(--maxWidth);
    left: 50%;
    transform: translateX(-50%);
  }
`;
const CloseButton = styled.button `
  ${ButtonReset};
  ${transition('opacity, transform')};

  position: absolute;
  top: 0;
  right: 0;
  color: var(--navigationFgActiveColour);
  width: ${fluid(16, 22, Device.TabletLarge)};
  height: ${fluid(16, 22, Device.TabletLarge)};

  [data-site='ess'] & {
    top: ${fluid(16, 22, Device.TabletLarge)};
  }

  > svg {
    display: block;
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 5px;
    outline-width: 2px;
    outline-style: solid;
  }

  &.focus-ring,
  &:hover {
    opacity: 0.7;
    transform: scale(1.1);
  }
`;
const NavigationDropdownInner = styled.div `
  margin: 0 auto;
  display: grid;
  position: relative;
  grid-template-columns: repeat(4, 1fr);
`;
const DropdownNavigationLink = styled.a `
  ${transition('background-size')};
  ${fonts.daxWide.medium};

  display: flex;
  align-items: center;
  font-size: ${fluid(16, 18)};
  line-height: ${fluid(26, 28)};
  min-height: ${fluid(42, 48)};
  text-decoration: none;
  color: var(--navigationFgActiveColour);
  background: var(--navigationBgActiveColour);
  background-image: linear-gradient(to right, var(--dropdownLinkBgActiveColour), var(--dropdownLinkBgActiveColour));
  background-repeat: no-repeat;
  background-size: 0% 100%;
  padding: 0 8px 0 ${fluid(16, 24, Device.TabletLarge)};

  &.focus-ring {
    outline: auto;
    outline-offset: -3px;
    outline-width: 1px;
    outline-style: solid;
  }

  &.focus-ring,
  &[data-active='true'],
  &:hover {
    background-size: 100% 100%;
  }
`;
const NavigationDropdownColumn = styled.ul `
  ${transition('opacity', 0.5)};

  grid-row: 1 / span 1;
  border-right: 1px solid var(--dropdownBorderColour);

  [data-site='ess'] & {
    padding: ${fluid(52, 64, Device.TabletLarge)} 0;
  }

  &[data-featured='true'] {
    opacity: 1;
    margin-left: auto;
    padding-left: ${fluid(18, 24, Device.TabletLarge)};
    border: 0;
  }

  &[data-column='1'] {
    grid-column: 1 / span 1;

    ${DropdownNavigationLink} {
      padding-left: ${fluid(16, 32)};

      [data-site='ess'] & {
        ${transition('transform, color', 0.8)};
        ${fonts.daxWide.bold};

        font-size: ${fluid(22, 26, Device.TabletLarge)};
        line-height: ${fluid(24, 28, Device.TabletLarge)};
        background: none;
        transform: translateX(calc(${fluid(40, 44, Device.TabletLarge)} * -1));
        margin-bottom: 12px;

        &[data-has-children='true']::after {
          ${transition('max-width', 1.33)};

          display: block;
          flex: 1;
          content: '';
          height: 1px;
          background-color: var(--dropdownBorderColour);
          max-width: 0;
          margin-left: ${fluid(16, 20, Device.TabletLarge)};
        }

        > svg {
          ${transition('color', 0.8)};

          display: block;
          height: ${fluid(30, 34, Device.TabletLarge)};
          color: var(--navigationBgActiveColour);
          margin-right: 10px;
        }

        &.focus-ring,
        &[data-active='true'],
        &:hover {
          color: var(--dropdownLinkFgActiveColour);
          transform: none;
          padding-right: 0;

          > svg {
            color: var(--dropdownLinkFgActiveColour);
          }

          &::after {
            max-width: 300px;
          }
        }
      }

      [data-site='mep'] & {
        ${fonts.museoSlab.bold};

        font-size: ${fluid(18, 20)};
      }
    }
  }

  &[data-column='2'] {
    grid-column: 2 / span 1;
  }

  &[data-column='3'] {
    grid-column: 3 / span 1;
  }

  &[data-column='4'] {
    grid-column: 4 / span 1;
  }

  &[data-active='true'] {
    opacity: 1;
    z-index: 1;
  }

  &[data-active='false'] {
    opacity: 0;
  }

  &[data-active] {
    ${ButtonStyles.Container} {
      padding-left: ${fluid(20, 24, Device.TabletLarge)};
      padding-right: ${fluid(20, 24, Device.TabletLarge)};

      [data-site='mep'] & {
        line-height: ${fluid(26, 28)};
        min-height: ${fluid(42, 48)};
        margin-bottom: 0;
      }
    }
  }

  > li > ${ButtonStyles.Container} {
    margin-bottom: ${fluid(12, 16)};
  }
`;
const FeaturedCard = styled.a `
  display: block;
  background-color: var(--featuredCardBgColour);
  color: var(--featuredCardFgColour);
  text-decoration: none;

  [data-site='ess'] & {
    padding: ${fluid(16, 20)} ${fluid(16, 20)} ${fluid(32, 40)};
  }

  &:focus {
    outline: none;
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 5px;
    outline-width: 2px;
    outline-style: solid;
  }

  &.focus-ring,
  &:hover {
    ${ButtonStyles.IconWrapper} {
      transform: translateX(15%);
    }
  }

  ${ButtonStyles.Container} {
    margin: ${fluid(14, 18, Device.TabletLarge)} 0 0;

    [data-site='mep'] & {
      margin-top: ${fluid(20, 26, Device.TabletLarge)};
    }
  }
`;
const FeaturedCardImageWrapper = styled.div `
  ${imageWrapperPreserveRatio(426, 268)};

  [data-site='ess'] & {
    margin: 0 0 ${fluid(32, 36, Device.TabletLarge)};
  }

  [data-site='mep'] & {
    border-radius: 8px;
    margin: ${fluid(38, 46, Device.TabletLarge)} 0 ${fluid(22, 26, Device.TabletLarge)};
  }
`;
const FeaturedCardImage = styled.img `
  ${transition('transform')};
  ${imageCoverContainer()};

  ${FeaturedCard}.focus-ring &,
  ${FeaturedCard}:hover & {
    transform: scale(1.05);
  }
`;
const FeaturedCardTag = styled.span `
  position: absolute;
  background-color: var(--featuredCardTagBgColour);
  color: var(--featuredCardTagFgColour);

  [data-site='ess'] & {
    ${fonts.daxWide.medium};

    font-size: ${fluid(14, 16, Device.TabletLarge)};
    line-height: ${fluid(24, 26, Device.TabletLarge)};
    top: 0;
    right: 0;
    padding: ${fluid(8, 12, Device.TabletLarge)} ${fluid(18, 24, Device.TabletLarge)};
  }

  [data-site='mep'] & {
    ${fonts.museoSlab.medium};

    font-size: ${fluid(18, 20, Device.TabletLarge)};
    line-height: ${fluid(28, 30, Device.TabletLarge)};
    bottom: 0;
    left: 0;
    /* eslint-disable */
    padding: ${fluid(8, 12, Device.TabletLarge)} ${fluid(38, 46, Device.TabletLarge)} ${fluid(8, 12, Device.TabletLarge)}
      ${fluid(16, 20, Device.TabletLarge)};
    /* eslint-enable */
    clip-path: polygon(
      100% 100%,
      100% 92%,
      calc(100% - 1px) 79%,
      calc(100% - 2px) 71%,
      calc(100% - 4px) 64%,
      calc(100% - 14px) 42%,
      calc(100% - 32px) 0%,
      0% 0%,
      0% 100%
    );
  }
`;
const FeaturedCardTitle = styled.h3 `
  [data-site='ess'] & {
    ${fonts.daxWide.medium};

    font-size: ${fluid(22, 26, Device.TabletLarge)};
    line-height: ${fluid(32, 36, Device.TabletLarge)};
    margin: 0 0 ${fluid(16, 18, Device.TabletLarge)};
  }

  [data-site='mep'] & {
    ${fonts.museoSlab.bold};

    font-size: ${fluid(18, 20, Device.TabletLarge)};
    line-height: ${fluid(28, 30, Device.TabletLarge)};
    margin: 0 0 ${fluid(18, 20, Device.TabletLarge)};
  }
`;
const FeaturedCardContent = styled.p `
  ${fonts.daxWide.regular};

  font-size: ${fluid(16, 18, Device.TabletLarge)};
  line-height: ${fluid(26, 28, Device.TabletLarge)};
  margin: 0;
`;
const DesktopNavigationStyles = {
    CloseButton,
    Container,
    DropdownNavigationLink,
    FeaturedCard,
    FeaturedCardContent,
    FeaturedCardImage,
    FeaturedCardImageWrapper,
    FeaturedCardTag,
    FeaturedCardTitle,
    NavigationDropdown,
    NavigationDropdownColumn,
    NavigationDropdownInner,
    NavigationLink,
    DropdownBackground,
};
export default DesktopNavigationStyles;
